/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Module Marketplace — Org Admins discover available apps and request access.
 */

import React, { useState, useEffect } from 'react';
import {
  Package, BarChart3, Globe, BookOpen, LifeBuoy, X, Send, Clock,
  CheckCircle2, XCircle, ShoppingBag, Search, Boxes, Banknote,
  HardDrive, Shield, Palette,
} from 'lucide-react';
import {
  APP_REGISTRY, Organization, ModuleRequest, ModuleRequestStatus,
  NotificationType, User, UserRole,
} from '../domain/entities';
import { getRepository } from '../config/repository';
import { cn } from '../ui/utils';

// ─── Icon resolver (mirrors SaasAdminPage AppIcon) ───────────────────────────

const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  Package, BarChart3, Globe, BookOpen, LifeBuoy, Boxes, Banknote,
};

function AppIcon({ iconKey, size = 24 }: { iconKey: string; size?: number }) {
  const Icon = ICON_MAP[iconKey] ?? Package;
  return <Icon size={size} />;
}

// ─── Component ───────────────────────────────────────────────────────────────

const repo = getRepository();

export function MarketplacePage({
  currentOrg,
  user,
}: {
  currentOrg: Organization | null;
  user: User | null;
}) {
  const [requests, setRequests] = useState<ModuleRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestModal, setRequestModal] = useState<{ moduleId: string; moduleName: string } | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const load = async () => {
    if (!currentOrg) return;
    const data = await repo.getModuleRequests(currentOrg.id);
    setRequests(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [currentOrg?.id]);

  const enabledApps = currentOrg?.enabledApps ?? [];

  const getStatus = (appId: string): 'installed' | 'pending' | 'available' => {
    if (enabledApps.includes(appId)) return 'installed';
    if (requests.some(r => r.moduleId === appId && r.status === ModuleRequestStatus.PENDING)) return 'pending';
    return 'available';
  };

  const handleSubmitRequest = async () => {
    if (!currentOrg || !user || !requestModal) return;
    setSubmitting(true);

    const request: ModuleRequest = {
      id: crypto.randomUUID(),
      orgId: currentOrg.id,
      orgName: currentOrg.name,
      moduleId: requestModal.moduleId,
      moduleName: requestModal.moduleName,
      requestedBy: user.id,
      requestedByName: user.name,
      status: ModuleRequestStatus.PENDING,
      message: message.trim(),
      createdAt: Date.now(),
    };

    await repo.saveModuleRequest(request);

    // Notify all Super Admins
    const allUsers = await repo.getUsers();
    const superAdmins = allUsers.filter(u => u.role === UserRole.SUPER_ADMIN);
    for (const sa of superAdmins) {
      await repo.saveNotification({
        id: crypto.randomUUID(),
        recipientId: sa.id,
        actorId: user.id,
        type: NotificationType.NEW_REQUEST,
        title: 'New Module Access Request',
        message: `${currentOrg.name} has requested access to ${requestModal.moduleName}.`,
        targetPath: '/saas-admin/organizations',
        isRead: false,
        createdAt: Date.now(),
        orgId: currentOrg.id,
      });
    }

    setMessage('');
    setRequestModal(null);
    setSubmitting(false);
    await load();
  };

  // Module overrides — future: load from server API. Currently unused (always empty).
  const moduleOverrides: Record<string, { maturity?: string; publiclyAvailable?: boolean }> = React.useMemo(() => ({}), []);

  const isPubliclyVisible = (app: typeof APP_REGISTRY[number]) => {
    const ov = moduleOverrides[app.id];
    const maturity = ov?.maturity ?? app.maturity;
    const pub = ov?.publiclyAvailable ?? app.publiclyAvailable;
    return pub && maturity !== 'DEVELOPMENT';
  };

  // Filter apps with search — hide DEVELOPMENT and non-public modules
  const filteredApps = APP_REGISTRY.filter(app =>
    isPubliclyVisible(app) && (
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const statusBadge = (status: 'installed' | 'pending' | 'available') => {
    switch (status) {
      case 'installed':
        return <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1"><CheckCircle2 size={10} /> Installed</span>;
      case 'pending':
        return <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1"><Clock size={10} /> Pending</span>;
      case 'available':
        return <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest bg-blue-100 text-blue-700 border border-blue-200">Available</span>;
    }
  };

  const requestStatusBadge = (status: ModuleRequestStatus) => {
    switch (status) {
      case ModuleRequestStatus.PENDING:
        return <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200">Pending</span>;
      case ModuleRequestStatus.APPROVED:
        return <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 border border-emerald-200">Approved</span>;
      case ModuleRequestStatus.REJECTED:
        return <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-red-100 text-red-700 border border-red-200">Rejected</span>;
    }
  };

  if (!currentOrg) return <div className="p-20 text-center text-slate-400 italic">No organization selected.</div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center">
              <ShoppingBag size={20} />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-zinc-900">Explore Apps</h2>
              <p className="text-sm text-slate-500 mt-0.5">Discover modules available for your organization.</p>
            </div>
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search apps..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none w-64"
          />
        </div>
      </div>

      {/* App Gallery */}
      {loading ? (
        <div className="p-20 text-center text-slate-400 italic">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredApps.map(app => {
            const status = getStatus(app.id);
            const isComingSoon = app.status === 'coming_soon';
            return (
              <div
                key={app.id}
                className={cn(
                  'relative rounded-2xl border p-6 transition-all duration-200',
                  isComingSoon
                    ? 'border-zinc-100 bg-zinc-50 opacity-60'
                    : status === 'installed'
                      ? 'border-emerald-200 bg-emerald-50/30'
                      : status === 'pending'
                        ? 'border-amber-200 bg-amber-50/30'
                        : 'border-zinc-200 bg-white hover:border-indigo-200 hover:shadow-md'
                )}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center',
                    isComingSoon ? 'bg-zinc-200 text-zinc-400'
                      : status === 'installed' ? 'bg-emerald-100 text-emerald-600'
                        : 'bg-indigo-100 text-indigo-600'
                  )}>
                    <AppIcon iconKey={app.iconKey} size={22} />
                  </div>
                  <div>
                    {isComingSoon
                      ? <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest bg-zinc-200 text-zinc-500">Coming Soon</span>
                      : statusBadge(status)
                    }
                  </div>
                </div>

                <h3 className="text-base font-bold text-zinc-900 mb-1">{app.name}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed mb-5">{app.description}</p>

                {!isComingSoon && status === 'available' && (
                  <button
                    onClick={() => setRequestModal({ moduleId: app.id, moduleName: app.name })}
                    className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Send size={14} /> Request Access
                  </button>
                )}
                {!isComingSoon && status === 'pending' && (
                  <div className="w-full py-2.5 px-4 rounded-xl text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 text-center">
                    Request under review
                  </div>
                )}
                {!isComingSoon && status === 'installed' && (
                  <div className="w-full py-2.5 px-4 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 text-center">
                    Active on your workspace
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Premium Platform Features */}
      <div>
        <h3 className="text-lg font-bold text-zinc-900 mb-4">Premium Platform Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Maintenance Center Card */}
          <div className="relative rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/30 p-6 transition-all duration-200 hover:shadow-md">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                <HardDrive size={22} />
              </div>
              <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-700 border border-amber-200">Pro Plan</span>
            </div>
            <h3 className="text-base font-bold text-zinc-900 mb-1">Maintenance & Recovery Center</h3>
            <p className="text-sm text-zinc-500 leading-relaxed mb-5">
              Backup, restore, and manage module data with audit-logged operations. Protect your organisation's data with downloadable archives.
            </p>
            <a href="/system/maintenance"
              className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors flex items-center justify-center gap-2">
              <Shield size={14} /> Open Maintenance Center
            </a>
          </div>

          {/* White-Label Branding Card */}
          <div className="relative rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50/50 to-indigo-50/30 p-6 transition-all duration-200 hover:shadow-md">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                <Palette size={22} />
              </div>
              <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest bg-purple-100 text-purple-700 border border-purple-200">Enterprise</span>
            </div>
            <h3 className="text-base font-bold text-zinc-900 mb-1">White-Label Branding</h3>
            <p className="text-sm text-zinc-500 leading-relaxed mb-5">
              Custom logo, favicon, and colour theming to match your brand identity across the entire platform.
            </p>
            <a href="/system/whitelabel"
              className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
              <Palette size={14} /> Open Branding Studio
            </a>
          </div>
        </div>
      </div>

      {/* Request History */}
      {requests.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-zinc-900 mb-4">Request History</h3>
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 text-left">
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Module</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Requested By</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Message</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Status</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Date</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">Admin Notes</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(req => (
                  <tr key={req.id} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                    <td className="px-5 py-3 font-medium text-zinc-900">{req.moduleName}</td>
                    <td className="px-5 py-3 text-zinc-600">{req.requestedByName}</td>
                    <td className="px-5 py-3 text-zinc-500 max-w-[200px] truncate">{req.message || '—'}</td>
                    <td className="px-5 py-3">{requestStatusBadge(req.status)}</td>
                    <td className="px-5 py-3 text-zinc-400 text-xs">{new Date(req.createdAt).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-zinc-500 max-w-[200px] truncate">{req.adminNotes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Request Access Modal */}
      {requestModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-zinc-100">
              <div>
                <h2 className="text-lg font-bold text-zinc-900">Request Access</h2>
                <p className="text-sm text-zinc-500 mt-0.5">{requestModal.moduleName}</p>
              </div>
              <button onClick={() => { setRequestModal(null); setMessage(''); }} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">
                  Message to Platform Admin (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell the platform team why you need this module..."
                  rows={4}
                  className="w-full px-4 py-3 text-sm border border-zinc-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none resize-none"
                  maxLength={500}
                />
                <p className="text-[10px] text-zinc-400 mt-1 text-right">{message.length}/500</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-700 leading-relaxed">
                  Your request will be sent to the Platform Admin for review. You'll receive a notification once a decision is made.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-100">
              <button
                onClick={() => { setRequestModal(null); setMessage(''); }}
                className="px-4 py-2.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRequest}
                disabled={submitting}
                className="px-6 py-2.5 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Send size={14} /> {submitting ? 'Sending...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
